import { computed, ref } from "vue"
import type { Ref } from "vue"

interface UseSwipeBackOptions {
  /** The two-pane strip (`w-[200%]`) that slides between its first and second pane. */
  stripRef: Ref<HTMLElement | null>
  /** Whether the strip is currently showing its second pane (the one a swipe goes back from). */
  isOnSecondPane: () => boolean
  onBack: () => void
}

const AXIS_LOCK_PX = 8
const BACK_DISTANCE_RATIO = 0.3
const BACK_VELOCITY_PX_PER_MS = 0.5

// Swipe right to go back on a mobile Drawer's list -> page slide strip (MobileProfileDrawer.vue,
// MobileSettingsPage.vue). The second pane follows the finger while it's dragged right; released past
// 30% of the pane's width (or with a quick flick) it slides on out to the first pane, otherwise it
// springs back.
// Axis-locked like the chat Drawer (pages/m-groups/[id].vue): the first AXIS_LOCK_PX of movement
// decide. A vertical gesture is left alone — the page's own scrolling and vaul's swipe-down-to-close
// keep working — while a rightward one is kept from reaching anything above the strip.
// Bind the handlers in the *bubble* phase: vaul listens on DrawerContent, above the strip, so it still
// runs after them; and with strips nested (the settings page's sections inside the profile Drawer's
// pages) the innermost one gets the gesture first, takes it and stops it, so one swipe only ever goes
// back one level.
// Gestures that start on a slider or a horizontal carousel are never taken: the volume slider in
// "Звуки" and the chat-style carousel in "Оформление" are dragged sideways.
export function useSwipeBack({ stripRef, isOnSecondPane, onBack }: UseSwipeBackOptions) {
  const offsetPx = ref(0)
  const isSwiping = ref(false)
  let start: { x: number, y: number, t: number, pointerId: number } | null = null
  let axis: "x" | "y" | null = null

  function onPointerDown(event: PointerEvent) {
    start = null
    if (!isOnSecondPane()) return
    if ((event.target as Element | null)?.closest('[data-slot="slider"], [data-horizontal-swipe]')) return
    start = { x: event.clientX, y: event.clientY, t: event.timeStamp, pointerId: event.pointerId }
    axis = null
  }

  function onPointerMove(event: PointerEvent) {
    if (!start || event.pointerId !== start.pointerId) return
    const dx = event.clientX - start.x
    const dy = event.clientY - start.y
    if (axis === null) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_PX) return
      axis = dx > 0 && Math.abs(dx) > Math.abs(dy) ? "x" : "y"
    }
    if (axis !== "x") return
    event.stopPropagation()
    isSwiping.value = true
    offsetPx.value = Math.max(0, dx)
  }

  function onPointerEnd(event: PointerEvent) {
    if (start && axis === "x" && event.type === "pointerup") {
      const paneWidth = (stripRef.value?.offsetWidth ?? 0) / 2
      const velocity = offsetPx.value / Math.max(1, event.timeStamp - start.t)
      if (offsetPx.value > paneWidth * BACK_DISTANCE_RATIO || velocity > BACK_VELOCITY_PX_PER_MS) onBack()
    }
    start = null
    axis = null
    isSwiping.value = false
    offsetPx.value = 0
  }

  // The strip's position: the first pane, the second, or the second pulled right by the finger — with
  // the transition off while it follows the finger, and back on for the settle/slide once it lets go.
  const stripStyle = computed(() => ({
    transform: isOnSecondPane() ? `translateX(calc(-50% + ${offsetPx.value}px))` : "translateX(0%)",
    transition: isSwiping.value ? "none" : undefined,
  }))

  return {
    stripStyle,
    // For `v-on="handlers"` on the strip.
    handlers: {
      pointerdown: onPointerDown,
      pointermove: onPointerMove,
      pointerup: onPointerEnd,
      pointercancel: onPointerEnd,
    },
  }
}
